import React, { useState, useEffect, useRef } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import './FilterDropdown.css';

function highlightMatch(label, query) {
  if (!query.trim()) return label;
  const idx = label.toLowerCase().indexOf(query.trim().toLowerCase());
  if (idx === -1) return label;
  const end = idx + query.trim().length;
  return (
    <>
      {label.slice(0, idx)}
      <mark className="fd-match">{label.slice(idx, end)}</mark>
      {label.slice(end)}
    </>
  );
}

const FilterDropdown = ({ value, options, onChange, align = 'right', searchable = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(''); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && searchable) setTimeout(() => searchRef.current?.focus(), 0);
    if (!open) setQuery('');
  }, [open, searchable]);

  const norm = options.map(o => ({ key: o.key ?? o.value, label: o.label }));
  const current = norm.find(o => o.key === value) || norm[0];

  const visible = searchable && query.trim()
    ? norm.filter(o => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : norm;

  useEffect(() => { setHighlightedIndex(0); }, [query, open]);

  const selectOption = (key) => { onChange(key); setOpen(false); setQuery(''); };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedIndex(i => Math.min(i + 1, visible.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (visible[highlightedIndex]) selectOption(visible[highlightedIndex].key);
    }
  };

  return (
    <div className="filter-dropdown" ref={ref}>
      <button type="button" className="filter-dropdown-btn" onClick={() => setOpen(o => !o)} title={current?.label}>
        <span className="fd-btn-label">{current?.label}</span>
        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} className={`fd-chevron ${open ? 'open' : ''}`} />
      </button>
      {open && (
        <div className={`filter-dropdown-menu align-${align}`}>
          {searchable && (
            <div className="fd-search-wrap">
              <input
                ref={searchRef}
                type="text"
                className="fd-search"
                placeholder="Search…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onClick={e => e.stopPropagation()}
              />
              {query && (
                <button
                  type="button"
                  className="fd-search-clear"
                  onClick={(e) => { e.stopPropagation(); setQuery(''); searchRef.current?.focus(); }}
                  title="Clear search"
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </button>
              )}
            </div>
          )}
          <div className="fd-options-list">
            {visible.length === 0 && <div className="fd-empty">No matches</div>}
            {visible.map((o, i) => (
              <div
                key={o.key}
                className={`fd-option ${o.key === value ? 'active' : ''} ${searchable && i === highlightedIndex ? 'highlighted' : ''}`}
                onMouseEnter={() => setHighlightedIndex(i)}
                onClick={() => selectOption(o.key)}
                title={o.label}
              >
                {searchable ? highlightMatch(o.label, query) : o.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;